import React, { useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Shield, ArrowRight, ArrowLeft, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import api from "@/lib/api";
import { CreceLogo } from "@/components/CreceLogo";

export default function AdminForgotPassword() {
  const [loading, setLoading] = useState(false);
  const [sent, setSent] = useState(null);
  const [devLink, setDevLink] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/admin-forgot-password");
      setSent(data);
      if (data.dev_mode && data.dev_reset_link) setDevLink(data.dev_reset_link);
      toast.success(data.message);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Something went wrong");
    } finally { setLoading(false); }
  };

  return (
    <div className="min-h-screen grid place-items-center bg-stone-900 p-6 relative overflow-hidden">
      <div className="absolute inset-0 opacity-20"
        style={{ background: "radial-gradient(circle at 30% 20%, #064E3B 0%, transparent 50%), radial-gradient(circle at 70% 80%, #D97706 0%, transparent 50%)" }} />
      <div className="w-full max-w-md relative">
        <div className="flex flex-col items-center mb-8">
          <CreceLogo size="lg" theme="light" />
          <div className="mt-3 flex items-center gap-2 text-xs font-bold uppercase tracking-[0.2em] text-amber-400">
            <Shield className="w-3.5 h-3.5" /> Admin Console
          </div>
        </div>
        <div className="bg-stone-50 rounded-2xl p-8 shadow-2xl">
          <h1 className="font-display text-3xl font-black tracking-tight text-stone-900">Forgot admin password?</h1>
          {!sent ? (
            <>
              <p className="text-sm text-stone-500 mt-2">A reset link will be sent to the configured admin recovery email. Click the button below to send it.</p>
              <form onSubmit={submit} className="mt-6">
                <Button type="submit" data-testid="admin-forgot-submit" disabled={loading}
                  className="w-full h-12 rounded-full bg-emerald-900 hover:bg-emerald-950 text-white font-bold">
                  {loading ? "Sending..." : "Send reset link"} <ArrowRight className="ml-2 w-4 h-4" />
                </Button>
              </form>
            </>
          ) : (
            <div className="mt-5 space-y-4" data-testid="admin-forgot-sent-state">
              <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-4 text-sm flex items-start gap-3">
                <Mail className="w-5 h-5 text-emerald-700 mt-0.5 shrink-0" />
                <div>
                  <div className="font-bold text-emerald-900">{sent.message}</div>
                  <p className="text-xs text-emerald-700 mt-1">Recovery email: <span className="font-mono">{sent.recovery_email}</span>. Check inbox and spam. The link expires in 60 minutes.</p>
                </div>
              </div>
              {devLink && (
                <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4 text-xs">
                  <div className="font-bold uppercase tracking-wider text-amber-800 mb-2">Dev mode — Gmail SMTP keys not set</div>
                  <p className="text-amber-900 mb-2">In production, set <code className="font-mono">GMAIL_USER</code> + <code className="font-mono">GMAIL_APP_PASSWORD</code> env vars to send real emails. For now, use this link directly:</p>
                  <a href={devLink} className="font-mono text-emerald-900 underline break-all" data-testid="admin-dev-reset-link">{devLink}</a>
                </div>
              )}
            </div>
          )}
          <div className="mt-6 pt-5 border-t border-stone-200 text-center">
            <Link to="/admin" className="inline-flex items-center gap-1.5 text-sm font-bold text-stone-700 hover:text-emerald-900" data-testid="back-to-admin-login">
              <ArrowLeft className="w-3.5 h-3.5" /> Back to admin sign in
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
